import { useState, useEffect, useRef, type UIEventHandler } from 'react';
import { createPortal } from 'react-dom';
import { Bot, Globe, GraduationCap, Settings, ChevronDown, Zap, Users, UserCheck, Clock, Compass, PanelLeft, Plus, Folder, MessageSquare, MoreHorizontal, Pencil, Trash2, FileEdit, Search, Lock } from 'lucide-react';
import ProjectEditModal from './ProjectEditModal';
import type { TabType } from '../App';
import { type BackendUserWithPlan } from '../userService';
import { apiClient } from '../apiClient';
import { getProjects, deleteProjects, renameProject } from '../projects';
import { getChatMetadatasForProject, getProjectlessChats, type ChatMetadata } from '../chatmetadata';
import { deleteChat, renameChat } from '../chatservice';
import './Sidebar.css';
import logo from '../assets/images/logo.png';
import logoText from '../assets/images/logo-text.png';

type Project = {
    id: string;
    name: string;
    description?: string;
    chats: Chat[];
    files?: import('../projects').ProjectFile[];
};

type Chat = {
    id: string;
    name: string;
    lastMessage?: string;
    updatedAt: string;
};

type SidebarProps = {
    activeTab: TabType;
    onTabChange: (tab: TabType, subTab?: string) => void;
    isOpen: boolean;
    collapsed: boolean;
    onToggleCollapse: () => void;
    educationSubTab?: string;
    networkingSubTab?: string;
    userData: BackendUserWithPlan | null;
    onChatSelect?: (projectId: string, chatId: string) => void;
    onNewDirectChat?: () => void;
    selectedChatId?: string;
    selectedProjectId?: string;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
};

function TypewriterText({ text, speed = 30, onDone }: { text: string; speed?: number; onDone?: () => void }) {
    const [displayed, setDisplayed] = useState('');
    const onDoneRef = useRef(onDone);
    onDoneRef.current = onDone;
    useEffect(() => {
        let i = 0;
        setDisplayed('');
        const interval = setInterval(() => {
            i++;
            setDisplayed(text.slice(0, i));
            if (i >= text.length) {
                clearInterval(interval);
                onDoneRef.current?.();
            }
        }, speed);
        return () => clearInterval(interval);
    }, [text, speed]);
    return <span className="chat-name typewriter">{displayed}<span className="typewriter-cursor">|</span></span>;
}

function Sidebar({ activeTab, onTabChange, isOpen, collapsed, onToggleCollapse, educationSubTab = 'bootcamp', networkingSubTab = 'connections', userData, onChatSelect, onNewDirectChat, selectedChatId, selectedProjectId, mobileOpen }: SidebarProps) {
    const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['assistant']));
    const [tooltip, setTooltip] = useState<{ top: number; left: number } | null>(null);
    const [userProfile, setUserProfile] = useState<{ name: string; initials: string; role: string }>({
        name: '',
        initials: '',
        role: '',
    });

    // ── RightPanel state ──
    const [projects, setProjects] = useState<Project[]>([]);
    const [directChats, setDirectChats] = useState<ChatMetadata[]>([]);
    const [directChatsMoreUrl, setDirectChatsMoreUrl] = useState<string | null>(null);
    const [isLoadingMoreDirectChats, setIsLoadingMoreDirectChats] = useState(false);
    const [, setLoading] = useState(true);
    const [confirmDeleteChatId, setConfirmDeleteChatId] = useState<string | null>(null);
    const [isDeletingChatId, setIsDeletingChatId] = useState<string | null>(null);
    const [confirmDeleteProjectId, setConfirmDeleteProjectId] = useState<string | null>(null);
    const [isDeletingProjectId, setIsDeletingProjectId] = useState<string | null>(null);
    const [confirmDeleteProjectChatId, setConfirmDeleteProjectChatId] = useState<{ projectId: string; chatId: string } | null>(null);
    const [isDeletingProjectChatId, setIsDeletingProjectChatId] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; type: 'project' | 'direct-chat' | 'project-chat'; id: string; projectId?: string } | null>(null);
    const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
    const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
    const [renameProjectValue, setRenameProjectValue] = useState('');
    const [isProjectsExpanded, setIsProjectsExpanded] = useState(true);
    const [isYourChatsExpanded, setIsYourChatsExpanded] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [apiSearchResults, setApiSearchResults] = useState<{ results: any[]; next: string | null } | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [animatingChatIds, setAnimatingChatIds] = useState<Set<string>>(new Set());
    const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
    const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

    useEffect(() => {
        async function fetchUserProfile() {
            try {
                const response = await apiClient.get('/api/profile/');
                const data = response.data;
                const firstName = data.first_name ?? '';
                const lastName = data.last_name ?? '';
                const name = [firstName, lastName].filter(Boolean).join(' ') || data.email || 'User';
                const initials = firstName && lastName
                    ? `${firstName[0]}${lastName[0]}`.toUpperCase()
                    : name.slice(0, 2).toUpperCase();
                setUserProfile({
                    name,
                    initials,
                    role: data.profile?.background ?? '',
                });
            } catch {
                setUserProfile({ name: 'User', initials: 'U', role: '' });
            }
        }
        fetchUserProfile();
    }, []);

    // ── Load projects and chats ──
    useEffect(() => {
        async function loadProjects() {
            try {
                const projectsResponse = await getProjects();
                const projectsWithChats = await Promise.all(
                    projectsResponse.projects.map(async (project) => {
                        const chatsResponse = await getChatMetadatasForProject(project.id);
                        return {
                            id: project.id,
                            name: project.name,
                            description: project.description,
                            files: project.files,
                            chats: chatsResponse.chatMetadatas.map(chat => ({
                                id: chat.id,
                                name: chat.name,
                                lastMessage: '',
                                updatedAt: 'Recently',
                            })),
                        };
                    })
                );
                setProjects(projectsWithChats);
                // Broadcast to App so ChatRoom can show project list in attach menu
                window.dispatchEvent(new CustomEvent('sidebar-projects-loaded', {
                    detail: { projects: projectsWithChats.map(p => ({ id: p.id, name: p.name })) }
                }));
                // Animate all project chat names
                const allChatIds = projectsWithChats.flatMap(p => p.chats.map(c => c.id));
                setAnimatingChatIds(prev => {
                    const next = new Set(prev);
                    allChatIds.forEach(id => next.add(id));
                    return next;
                });
            } catch (error) {
                console.error('Failed to load projects:', error);
            } finally {
                setLoading(false);
            }
        }

        async function loadDirectChatsFirstPage() {
            try {
                const response = await getProjectlessChats();

                setDirectChats((prev) => {
                    if (prev.length === 0) {
                        // Animate all direct chat names on first load
                        setAnimatingChatIds(p => {
                            const next = new Set(p);
                            response.chatMetadatas.forEach(c => next.add(c.id));
                            return next;
                        });
                        return response.chatMetadatas;
                    }
                    const incomingById = new Map(response.chatMetadatas.map((c) => [c.id, c] as const));
                    const updatedPrev = prev.map((p) => incomingById.get(p.id) ?? p);
                    const seen = new Set(updatedPrev.map((c) => c.id));
                    const appended = response.chatMetadatas.filter((c) => !seen.has(c.id));
                    return [...updatedPrev, ...appended];
                });

                setDirectChatsMoreUrl(response.next ?? null);
            } catch (error) {
                console.error('Failed to load direct chats:', error);
            }
        }

        loadProjects();
        loadDirectChatsFirstPage();

        const bumpDirectChatToTop = (chatId: string) => {
            setDirectChats((prev) => {
                const idx = prev.findIndex((c) => c.id === chatId);
                if (idx <= 0) return prev;
                const chat = prev[idx];
                return [chat, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
            });
        };

        const handleDirectChatCreated = (e: Event) => {
            const ce = e as CustomEvent<ChatMetadata | undefined>;
            const created = ce.detail;

            if (created && created.id) {
                setIsYourChatsExpanded(true);
                setAnimatingChatIds((prev) => new Set(prev).add(created.id));
                setDirectChats((prev) => {
                    const without = prev.filter((c) => c.id !== created.id);
                    return [created, ...without];
                });

                void loadDirectChatsFirstPage();
                return;
            }

            void loadDirectChatsFirstPage();
        };

        const handleDirectChatTouched = (e: Event) => {
            const ce = e as CustomEvent<{ chatId?: string } | undefined>;
            const chatId = ce.detail?.chatId;
            if (!chatId) return;
            bumpDirectChatToTop(chatId);
        };

        const bumpProjectToTop = (projectId: string) => {
            setProjects((prev) => {
                const idx = prev.findIndex((p) => p.id === projectId);
                if (idx <= 0) return prev;
                const project = prev[idx];
                return [project, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
            });
        };

        const handleProjectChatCreated = (e: Event) => {
            const ce = e as CustomEvent<{ projectId: string; chat: { id: string; name: string } } | undefined>;
            const detail = ce.detail;
            if (!detail) return;
            setAnimatingChatIds((prev) => new Set(prev).add(detail.chat.id));
            setProjects((prev) =>
                prev.map((p) =>
                    p.id === detail.projectId
                        ? { ...p, chats: [{ id: detail.chat.id, name: detail.chat.name, updatedAt: 'Just now' }, ...p.chats] }
                        : p
                )
            );
            // Move the project to the top
            bumpProjectToTop(detail.projectId);
            setExpandedProjects((prev) => {
                const next = new Set(prev);
                next.add(detail.projectId);
                return next;
            });
        };

        const handleProjectChatTouched = (e: Event) => {
            const ce = e as CustomEvent<{ projectId?: string; chatId?: string } | undefined>;
            const projectId = ce.detail?.projectId;
            const chatId = ce.detail?.chatId;
            if (!projectId || !chatId) return;
            // Bump chat to top within project
            setProjects((prev) =>
                prev.map((p) => {
                    if (p.id !== projectId) return p;
                    const idx = p.chats.findIndex((c) => c.id === chatId);
                    if (idx <= 0) return p;
                    const chat = p.chats[idx];
                    return { ...p, chats: [chat, ...p.chats.slice(0, idx), ...p.chats.slice(idx + 1)] };
                })
            );
            // Bump project to top
            bumpProjectToTop(projectId);
        };

        const handleOpenCreateProjectModal = () => setShowCreateProjectModal(true);

        const handleChatMoved = (e: Event) => {
            const { chatId: movedChatId, fromProjectId, toProjectId } = (e as CustomEvent).detail;
            const now = new Date().toISOString();

            if (fromProjectId && toProjectId) {
                // Project → Project
                setProjects(prev => {
                    const source = prev.find(p => p.id === fromProjectId);
                    const chat = source?.chats.find(c => c.id === movedChatId);
                    if (!chat) return prev;
                    return prev.map(p => {
                        if (p.id === fromProjectId) return { ...p, chats: p.chats.filter(c => c.id !== movedChatId) };
                        if (p.id === toProjectId) return { ...p, chats: [chat, ...p.chats] };
                        return p;
                    });
                });
            } else if (fromProjectId && !toProjectId) {
                // Project → Direct chats
                setProjects(prev => {
                    const source = prev.find(p => p.id === fromProjectId);
                    const chat = source?.chats.find(c => c.id === movedChatId);
                    if (chat) setDirectChats(dc => [{ id: chat.id, name: chat.name, updatedAt: now }, ...dc]);
                    return prev.map(p =>
                        p.id === fromProjectId ? { ...p, chats: p.chats.filter(c => c.id !== movedChatId) } : p
                    );
                });
            } else if (!fromProjectId && toProjectId) {
                // Direct chats → Project
                const chat = directChats.find(c => c.id === movedChatId);
                if (chat) {
                    setDirectChats(prev => prev.filter(c => c.id !== movedChatId));
                    setProjects(prev => prev.map(p =>
                        p.id === toProjectId
                            ? { ...p, chats: [{ id: chat.id, name: chat.name, updatedAt: now }, ...p.chats] }
                            : p
                    ));
                }
            }
        };

        window.addEventListener('direct-chat-created', handleDirectChatCreated as EventListener);
        window.addEventListener('direct-chat-touched', handleDirectChatTouched as EventListener);
        window.addEventListener('project-chat-created', handleProjectChatCreated as EventListener);
        window.addEventListener('project-chat-touched', handleProjectChatTouched as EventListener);
        window.addEventListener('open-create-project-modal', handleOpenCreateProjectModal);
        window.addEventListener('chat-moved', handleChatMoved as EventListener);
        return () => {
            window.removeEventListener('direct-chat-created', handleDirectChatCreated as EventListener);
            window.removeEventListener('direct-chat-touched', handleDirectChatTouched as EventListener);
            window.removeEventListener('project-chat-created', handleProjectChatCreated as EventListener);
            window.removeEventListener('project-chat-touched', handleProjectChatTouched as EventListener);
            window.removeEventListener('open-create-project-modal', handleOpenCreateProjectModal);
            window.removeEventListener('chat-moved', handleChatMoved as EventListener);
        };
    }, []);

    const loadMoreDirectChats = async () => {
        if (!directChatsMoreUrl || isLoadingMoreDirectChats) return;

        try {
            setIsLoadingMoreDirectChats(true);
            const res = await getProjectlessChats(directChatsMoreUrl);
            setDirectChats((prev) => {
                const seen = new Set(prev.map((c) => c.id));
                const merged = [...prev];
                for (const c of res.chatMetadatas) {
                    if (!seen.has(c.id)) merged.push(c);
                }
                return merged;
            });
            setDirectChatsMoreUrl(res.next);
        } catch (error) {
            console.error('Failed to load more direct chats:', error);
        } finally {
            setIsLoadingMoreDirectChats(false);
        }
    };

    // Close context menu on outside click or scroll
    useEffect(() => {
        if (!contextMenu) return;
        const close = (e: Event) => {
            if (e instanceof MouseEvent) {
                const target = e.target as HTMLElement;
                if (target?.closest('.context-menu') || target?.closest('.chat-more-btn')) return;
            }
            setContextMenu(null);
        };
        document.addEventListener('mousedown', close);
        document.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('scroll', close, true);
        };
    }, [contextMenu]);

    const handleMoreBtnClick = (
        e: React.MouseEvent<HTMLButtonElement>,
        type: 'project' | 'direct-chat' | 'project-chat',
        id: string,
        projectId?: string
    ) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const menuWidth = 160;
        const menuHeight = 84;
        const x = rect.right - menuWidth;
        const y = rect.bottom + 4 + menuHeight > window.innerHeight ? rect.top - menuHeight - 4 : rect.bottom + 4;
        setContextMenu({ x, y, type, id, projectId });
    };

    const handleContextMenuDelete = () => {
        if (!contextMenu) return;
        setContextMenu(null);
        if (contextMenu.type === 'project') {
            setConfirmDeleteProjectId(contextMenu.id);
        } else if (contextMenu.type === 'direct-chat') {
            setConfirmDeleteChatId(contextMenu.id);
        } else if (contextMenu.type === 'project-chat' && contextMenu.projectId) {
            setConfirmDeleteProjectChatId({ projectId: contextMenu.projectId, chatId: contextMenu.id });
        }
    };

    const handleContextMenuRename = () => {
        if (!contextMenu) return;
        if (contextMenu.type === 'project') {
            const currentName = projects.find((p) => p.id === contextMenu.id)?.name || '';
            setContextMenu(null);
            setRenamingProjectId(contextMenu.id);
            setRenameProjectValue(currentName);
            return;
        }
        const chatId = contextMenu.id;
        let currentName = '';
        if (contextMenu.type === 'direct-chat') {
            currentName = directChats.find((c) => c.id === chatId)?.name || '';
        } else if (contextMenu.type === 'project-chat' && contextMenu.projectId) {
            const proj = projects.find((p) => p.id === contextMenu.projectId);
            currentName = proj?.chats.find((c) => c.id === chatId)?.name || '';
        }
        setContextMenu(null);
        setRenamingChatId(chatId);
        setRenameValue(currentName);
    };

    const executeRenameProject = async (projectId: string) => {
        const trimmed = renameProjectValue.trim();
        if (!trimmed) {
            setRenamingProjectId(null);
            return;
        }
        try {
            await renameProject(projectId, trimmed);
            setProjects((prev) =>
                prev.map((p) => p.id === projectId ? { ...p, name: trimmed } : p)
            );
        } catch (error) {
            console.error('Failed to rename project:', error);
        } finally {
            setRenamingProjectId(null);
        }
    };

    const executeRenameChat = async (chatId: string, projectId?: string) => {
        const trimmed = renameValue.trim();
        if (!trimmed) {
            setRenamingChatId(null);
            return;
        }
        try {
            await renameChat(chatId, trimmed);
            if (projectId) {
                setProjects((prev) =>
                    prev.map((p) =>
                        p.id === projectId
                            ? { ...p, chats: p.chats.map((c) => c.id === chatId ? { ...c, name: trimmed } : c) }
                            : p
                    )
                );
            } else {
                setDirectChats((prev) =>
                    prev.map((c) => c.id === chatId ? { ...c, name: trimmed } : c)
                );
            }
        } catch (error) {
            console.error('Failed to rename chat:', error);
        } finally {
            setRenamingChatId(null);
        }
    };

    const toggleProject = (projectId: string) => {
        setExpandedProjects((prev) => {
            const next = new Set(prev);
            if (next.has(projectId)) {
                next.delete(projectId);
            } else {
                next.add(projectId);
            }
            return next;
        });
    };

    // Combine all chats (direct + project) into a single recent list sorted by updatedAt
    const sq = searchQuery.toLowerCase().trim();
    const allRecentChats = [
        ...directChats.map(c => ({ id: c.id, name: c.name, updatedAt: '', projectId: '', projectName: '' })),
        ...projects.flatMap(p => p.chats.map(c => ({ id: c.id, name: c.name, updatedAt: c.updatedAt || '', projectId: p.id, projectName: p.name }))),
    ]
    .filter(c => !sq || c.name?.toLowerCase().includes(sq) || c.projectName?.toLowerCase().includes(sq))
    .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());

    const filteredProjects = sq
        ? projects.filter(p => p.name.toLowerCase().includes(sq) || p.chats.some(c => c.name?.toLowerCase().includes(sq)))
        : projects;

    // Debounced backend search — fires 300ms after user stops typing
    const handleSearchChange = (query: string) => {
        setSearchQuery(query);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        if (!query.trim() || query.trim().length < 2) {
            setApiSearchResults(null);
            setIsSearching(false);
            return;
        }
        setIsSearching(true);
        searchDebounceRef.current = setTimeout(async () => {
            try {
                const res = await apiClient.get(`/api/search/?q=${encodeURIComponent(query.trim())}`);
                setApiSearchResults(res.data);
            } catch {
                // Fallback to client-side only
                setApiSearchResults(null);
            }
            setIsSearching(false);
        }, 500);
    };

    const closeSidebarOnMobile = () => {
        if (window.innerWidth <= 768 && !collapsed) {
            onToggleCollapse();
        }
    };

    const handleCreateChat = (projectId: string) => {
        onTabChange('assistant');
        onChatSelect?.(projectId, '');
    };

    const executeDeleteDirectChat = async (chatId: string) => {
        try {
            setIsDeletingChatId(chatId);
            await deleteChat(chatId);
            setDirectChats((prev) => prev.filter((c) => c.id !== chatId));
            if (selectedChatId === chatId) {
                onNewDirectChat?.();
            }
        } catch (error) {
            console.error('Failed to delete direct chat:', error);
        } finally {
            setIsDeletingChatId(null);
            setConfirmDeleteChatId(null);
        }
    };

    const executeDeleteProject = async (projectId: string) => {
        try {
            setIsDeletingProjectId(projectId);
            const deletedProject = projects.find((p) => p.id === projectId);
            await deleteProjects([projectId]);
            setProjects((prev) => prev.filter((p) => p.id !== projectId));
            setExpandedProjects((prev) => {
                const next = new Set(prev);
                next.delete(projectId);
                return next;
            });
            if (deletedProject && selectedChatId && deletedProject.chats.some((c) => c.id === selectedChatId)) {
                onNewDirectChat?.();
            }
        } catch (error) {
            console.error('Failed to delete project:', error);
        } finally {
            setIsDeletingProjectId(null);
            setConfirmDeleteProjectId(null);
        }
    };

    const executeDeleteProjectChat = async (projectId: string, chatId: string) => {
        try {
            setIsDeletingProjectChatId(chatId);
            await deleteChat(chatId);
            setProjects((prev) =>
                prev.map((p) =>
                    p.id === projectId
                        ? { ...p, chats: p.chats.filter((c) => c.id !== chatId) }
                        : p
                )
            );
            if (selectedChatId === chatId) {
                onNewDirectChat?.();
            }
        } catch (error) {
            console.error('Failed to delete project chat:', error);
        } finally {
            setIsDeletingProjectChatId(null);
            setConfirmDeleteProjectChatId(null);
        }
    };

    const handleMainScroll: UIEventHandler<HTMLDivElement> = (e) => {
        if (!isYourChatsExpanded) return;
        if (!directChatsMoreUrl || isLoadingMoreDirectChats) return;

        const el = e.currentTarget;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        if (distanceFromBottom < 160) {
            void loadMoreDirectChats();
        }
    };

    const showTooltip = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setTooltip({ top: rect.top + rect.height / 2, left: rect.right + 12 });
    };
    const hideTooltip = () => setTooltip(null);

    const handleToggle = () => {
        hideTooltip();
        onToggleCollapse();
    };

    // Derive user display info from userData prop (passed from App), fall back to API-fetched profile
    const displayName = userData
        ? (userData.first_name || userData.last_name
            ? `${userData.first_name ?? ''} ${userData.last_name ?? ''}`.trim()
            : userData.username)
        : (userProfile.name || 'Loading...');

    const initials = userData
        ? (userData.first_name && userData.last_name
            ? `${userData.first_name[0]}${userData.last_name[0]}`.toUpperCase()
            : userData.username.substring(0, 2).toUpperCase())
        : (userProfile.initials || '??');

    const userRole = userData?.profile?.background
        ? (userData.profile.background.length > 25
            ? userData.profile.background.substring(0, 22) + '...'
            : userData.profile.background)
        : (userProfile.role || 'Chiefy Member');

    return (
        <>
        <aside className={`sidebar ${isOpen ? 'open' : 'closed'} ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
                {/* Logo/Brand */}
                <div className="sidebar-header">
                    <div className="brand">
                        <div
                            className="brand-logo"
                            onMouseEnter={collapsed ? showTooltip : undefined}
                            onMouseLeave={collapsed ? hideTooltip : undefined}
                        >
                            <img src={logo} alt="Chiefy Logo" className="logo-image" />
                            {collapsed && (
                                <button
                                    type="button"
                                    className="sidebar-toggle-overlay"
                                    onClick={handleToggle}
                                >
                                    <PanelLeft size={18} />
                                </button>
                            )}
                        </div>
                        {!collapsed && (
                            <div className="brand-text">
                                <img src={logoText} alt="Chiefy" className="logo-text-image" />
                            </div>
                        )}
                    </div>
                    {!collapsed && (
                        <button
                            type="button"
                            className="icon-btn sidebar-toggle"
                            onClick={handleToggle}
                            title="Close sidebar"
                        >
                            <PanelLeft size={18} />
                        </button>
                    )}
                </div>

                {/* Navigation */}
                <nav className="sidebar-nav">
                    <div className="nav-section">

                        {/* Assistant Tab with Submenu */}
                        <div className="nav-item-with-submenu desktop-submenu">
                            <button
                                type="button"
                                className={`nav-item ${activeTab === 'assistant' ? 'active' : ''} ${expandedMenus.has('assistant') ? 'expanded' : ''}`}
                                title={collapsed ? 'Assistant' : undefined}
                                onClick={() => {
                                    if (collapsed) {
                                        onToggleCollapse();
                                        onTabChange('assistant');
                                        setExpandedMenus(prev => new Set(prev).add('assistant'));
                                        return;
                                    }
                                    if (activeTab === 'assistant') {
                                        setExpandedMenus(prev => { const next = new Set(prev); next.has('assistant') ? next.delete('assistant') : next.add('assistant'); return next; });
                                    } else {
                                        onTabChange('assistant');
                                        // Collapse other menus, expand this one
                                        setExpandedMenus(new Set(['assistant']));
                                    }
                                }}
                            >
                                <span className="nav-icon"><Bot size={18} /></span>
                                {!collapsed && (
                                    <div className="nav-content">
                                        <span className="nav-label">Assistant</span>
                                    </div>
                                )}
                                {!collapsed && (
                                    <span className={`expand-icon ${expandedMenus.has('assistant') ? 'expanded' : ''}`}>
                                        <ChevronDown size={16} />
                                    </span>
                                )}
                                {activeTab === 'assistant' && <span className="active-indicator" />}
                            </button>
                            {!collapsed && expandedMenus.has('assistant') && (
                            <div className="submenu sidebar-chat-panel" onScroll={handleMainScroll}>
                                    {/* New Chat + Search — same style as submenu items */}
                                    <button
                                        type="button"
                                        className={`submenu-item ${!selectedChatId && !selectedProjectId ? 'active' : ''}`}
                                        onClick={() => { onNewDirectChat?.(); closeSidebarOnMobile(); }}
                                    >
                                        <Plus size={16} />
                                        <span>New Chat</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="submenu-item"
                                        onClick={() => { closeSidebarOnMobile(); setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 100); }}
                                    >
                                        <Search size={14} />
                                        <span>Search</span>
                                    </button>


                                    {/* Projects header with toggle + add */}
                                    <div className="submenu-section-header">
                                        <button
                                            type="button"
                                            className="submenu-section-toggle"
                                            onClick={() => setIsProjectsExpanded((v) => !v)}
                                        >
                                            <ChevronDown size={12} className={`submenu-chevron ${isProjectsExpanded ? 'expanded' : ''}`} />
                                            <span>Projects</span>
                                            {filteredProjects.length > 0 && <span className="submenu-section-count">{filteredProjects.length}</span>}
                                        </button>
                                        <button
                                            type="button"
                                            className="submenu-section-add"
                                            title="New Project"
                                            onClick={() => setShowCreateProjectModal(true)}
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>

                                    {/* Projects list */}
                                    {isProjectsExpanded && filteredProjects.map((project) => (
                                        <div key={project.id}>
                                            <div className="submenu-item-wrap">
                                                {renamingProjectId === project.id ? (
                                                    <div className="submenu-item submenu-item--indent">
                                                        <ChevronDown size={12} className="submenu-chevron" style={{visibility: 'hidden'}} />
                                                        <Folder size={14} />
                                                        <input
                                                            className="submenu-rename-input"
                                                            value={renameProjectValue}
                                                            placeholder="Project name"
                                                            autoFocus
                                                            onChange={(e) => setRenameProjectValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') executeRenameProject(project.id);
                                                                if (e.key === 'Escape') setRenamingProjectId(null);
                                                            }}
                                                            onBlur={() => executeRenameProject(project.id)}
                                                        />
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className={`submenu-item submenu-item--indent ${selectedProjectId === project.id ? 'active' : ''}`}
                                                        onClick={() => { toggleProject(project.id); }}
                                                    >
                                                        <ChevronDown size={12} className={`submenu-chevron ${expandedProjects.has(project.id) ? 'expanded' : ''}`} />
                                                        <Folder size={14} />
                                                        <span>{project.name}</span>
                                                    </button>
                                                )}
                                                {renamingProjectId !== project.id && (
                                                    <button
                                                        type="button"
                                                        className="submenu-more-btn"
                                                        title="More options"
                                                        onClick={(e) => { e.stopPropagation(); handleMoreBtnClick(e, 'project', project.id); }}
                                                    >
                                                        <MoreHorizontal size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            {expandedProjects.has(project.id) && (
                                                <div className="submenu-nested">
                                                    {project.chats.map((chat) => (
                                                        <div key={chat.id} className="submenu-item-wrap">
                                                            {renamingChatId === chat.id ? (
                                                                <div className="submenu-item submenu-item--deep">
                                                                    <MessageSquare size={12} />
                                                                    <input
                                                                        className="submenu-rename-input"
                                                                        value={renameValue}
                                                                        placeholder="Chat name"
                                                                        autoFocus
                                                                        onChange={(e) => setRenameValue(e.target.value)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') executeRenameChat(chat.id, project.id);
                                                                            if (e.key === 'Escape') setRenamingChatId(null);
                                                                        }}
                                                                        onBlur={() => executeRenameChat(chat.id, project.id)}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className={`submenu-item submenu-item--deep ${selectedChatId === chat.id ? 'active' : ''}`}
                                                                    onClick={() => { onChatSelect?.(project.id, chat.id); closeSidebarOnMobile(); }}
                                                                >
                                                                    <MessageSquare size={12} />
                                                                    {animatingChatIds.has(chat.id) ? (
                                                                        <TypewriterText text={chat.name} onDone={() => setAnimatingChatIds(prev => { const next = new Set(prev); next.delete(chat.id); return next; })} />
                                                                    ) : (
                                                                        <span>{chat.name}</span>
                                                                    )}
                                                                </button>
                                                            )}
                                                            {renamingChatId !== chat.id && (
                                                                <button
                                                                    type="button"
                                                                    className="submenu-more-btn"
                                                                    title="More options"
                                                                    onClick={(e) => { e.stopPropagation(); handleMoreBtnClick(e, 'project-chat', chat.id, project.id); }}
                                                                >
                                                                    <MoreHorizontal size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className={`submenu-item submenu-item--deep ${selectedProjectId === project.id && !selectedChatId ? 'active' : 'submenu-item--muted'}`}
                                                        onClick={() => { handleCreateChat(project.id); closeSidebarOnMobile(); }}
                                                    >
                                                        <Plus size={12} />
                                                        <span>New Chat</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Chats header with toggle + add */}
                                    <div className="submenu-section-header">
                                        <button
                                            type="button"
                                            className="submenu-section-toggle"
                                            onClick={() => setIsYourChatsExpanded((v) => !v)}
                                        >
                                            <ChevronDown size={12} className={`submenu-chevron ${isYourChatsExpanded ? 'expanded' : ''}`} />
                                            <span>Recent Chats</span>
                                        </button>
                                    </div>

                                    {/* All recent chats (direct + project) */}
                                    {isYourChatsExpanded && allRecentChats.map((chat) => (
                                        <div key={chat.id} className="submenu-item-wrap">
                                            {renamingChatId === chat.id ? (
                                                <div className="submenu-item submenu-item--indent">
                                                    <MessageSquare size={14} />
                                                    <input
                                                        className="submenu-rename-input"
                                                        value={renameValue}
                                                        placeholder="Chat name"
                                                        autoFocus
                                                        onChange={(e) => setRenameValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') executeRenameChat(chat.id);
                                                            if (e.key === 'Escape') setRenamingChatId(null);
                                                        }}
                                                        onBlur={() => executeRenameChat(chat.id)}
                                                    />
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className={`submenu-item submenu-item--indent ${selectedChatId === chat.id ? 'active' : ''}`}
                                                    onClick={() => {
                                                        onChatSelect?.(chat.projectId, chat.id);
                                                        closeSidebarOnMobile();
                                                    }}
                                                >
                                                    <MessageSquare size={14} />
                                                    {animatingChatIds.has(chat.id) ? (
                                                        <TypewriterText text={chat.name || 'New chat'} onDone={() => setAnimatingChatIds(prev => { const next = new Set(prev); next.delete(chat.id); return next; })} />
                                                    ) : (
                                                        <span>{chat.name || 'New chat'}</span>
                                                    )}
                                                    {chat.projectName && <span className="chat-project-tag">{chat.projectName}</span>}
                                                </button>
                                            )}
                                            {renamingChatId !== chat.id && (
                                                <button
                                                    type="button"
                                                    className="submenu-more-btn"
                                                    title="More options"
                                                    onClick={(e) => { e.stopPropagation(); handleMoreBtnClick(e, 'direct-chat', chat.id); }}
                                                >
                                                    <MoreHorizontal size={14} />
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    {isLoadingMoreDirectChats && <div className="sp-loading-hint">Loading...</div>}
                            </div>
                        )}
                        </div>

                        {/* Education Tab with Submenu */}
                        <div className="nav-item-with-submenu desktop-submenu">
                            <button
                                type="button"
                                className={`nav-item ${activeTab === 'education' ? 'active' : ''} ${expandedMenus.has('education') ? 'expanded' : ''}`}
                                title={collapsed ? 'Education' : undefined}
                                onClick={() => {
                                    if (collapsed) {
                                        onToggleCollapse();
                                        onTabChange('education', 'bootcamp');
                                        setExpandedMenus(prev => new Set(prev).add('education'));
                                        return;
                                    }
                                    if (activeTab === 'education') {
                                        setExpandedMenus(prev => { const next = new Set(prev); next.has('education') ? next.delete('education') : next.add('education'); return next; });
                                    } else {
                                        onTabChange('education', 'bootcamp');
                                        setExpandedMenus(new Set(['education']));
                                    }
                                }}
                            >
                                <span className="nav-icon"><GraduationCap size={18} /></span>
                                {!collapsed && (
                                    <div className="nav-content">
                                        <span className="nav-label">Education</span>
                                        {userData?.access && userData.access.education === false && !userData.access.is_admin && (
                                            <Lock size={12} className="nav-lock-icon" />
                                        )}
                                    </div>
                                )}
                                {!collapsed && (
                                    <span className={`expand-icon ${expandedMenus.has('education') ? 'expanded' : ''}`}>
                                        <ChevronDown size={16} />
                                    </span>
                                )}
                                {activeTab === 'education' && <span className="active-indicator" />}
                            </button>
                            {!collapsed && expandedMenus.has('education') && (
                                <div className="submenu">
                                    <button
                                        type="button"
                                        className={`submenu-item submenu-item--indent ${educationSubTab === 'bootcamp' ? 'active' : ''}`}
                                        onClick={() => { onTabChange('education', 'bootcamp'); closeSidebarOnMobile(); }}
                                    >
                                        <Zap size={18} />
                                        <span>Bootcamp</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`submenu-item submenu-item--indent ${educationSubTab === 'coaching' ? 'active' : ''}`}
                                        onClick={() => { onTabChange('education', 'coaching'); closeSidebarOnMobile(); }}
                                    >
                                        <Users size={18} />
                                        <span>Coaching</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Networking Tab with Submenu */}
                        <div className="nav-item-with-submenu desktop-submenu">
                            <button
                                type="button"
                                className={`nav-item ${activeTab === 'networking' ? 'active' : ''} ${expandedMenus.has('networking') ? 'expanded' : ''}`}
                                title={collapsed ? 'Networking' : undefined}
                                onClick={() => {
                                    if (collapsed) {
                                        onToggleCollapse();
                                        onTabChange('networking', 'connections');
                                        setExpandedMenus(prev => new Set(prev).add('networking'));
                                        return;
                                    }
                                    if (activeTab === 'networking') {
                                        setExpandedMenus(prev => { const next = new Set(prev); next.has('networking') ? next.delete('networking') : next.add('networking'); return next; });
                                    } else {
                                        onTabChange('networking', 'connections');
                                        setExpandedMenus(new Set(['networking']));
                                    }
                                }}
                            >
                                <span className="nav-icon"><Globe size={18} /></span>
                                {!collapsed && (
                                    <div className="nav-content">
                                        <span className="nav-label">Networking</span>
                                        {userData?.access && userData.access.networking === false && !userData.access.is_admin && (
                                            <Lock size={12} className="nav-lock-icon" />
                                        )}
                                    </div>
                                )}
                                {!collapsed && (
                                    <span className={`expand-icon ${expandedMenus.has('networking') ? 'expanded' : ''}`}>
                                        <ChevronDown size={16} />
                                    </span>
                                )}
                                {activeTab === 'networking' && <span className="active-indicator" />}
                            </button>
                            {!collapsed && expandedMenus.has('networking') && (
                                <div className="submenu">
                                    <button
                                        type="button"
                                        className={`submenu-item submenu-item--indent ${networkingSubTab === 'connections' ? 'active' : ''}`}
                                        onClick={() => { onTabChange('networking', 'connections'); closeSidebarOnMobile(); }}
                                    >
                                        <UserCheck size={18} />
                                        <span>Connections</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`submenu-item submenu-item--indent ${networkingSubTab === 'requests' ? 'active' : ''}`}
                                        onClick={() => { onTabChange('networking', 'requests'); closeSidebarOnMobile(); }}
                                    >
                                        <Clock size={18} />
                                        <span>Requests</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`submenu-item submenu-item--indent ${networkingSubTab === 'discover' ? 'active' : ''}`}
                                        onClick={() => { onTabChange('networking', 'discover'); closeSidebarOnMobile(); }}
                                    >
                                        <Compass size={18} />
                                        <span>Discover</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`submenu-item submenu-item--indent ${networkingSubTab === 'groups' ? 'active' : ''}`}
                                        onClick={() => { onTabChange('networking', 'groups'); closeSidebarOnMobile(); }}
                                    >
                                        <Users size={18} />
                                        <span>Groups</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </nav>

                {/* User section at bottom */}
                <div className="sidebar-footer">
                    <button
                        type="button"
                        className={`user-section ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => { onTabChange('profile'); closeSidebarOnMobile(); }}
                        title={collapsed ? 'Profile' : undefined}
                    >
                        <div className="user-avatar">
                            <span>{initials}</span>
                        </div>
                        {!collapsed && (
                            <>
                                <div className="user-info">
                                    <span className="user-name">{displayName}</span>
                                    <span className="user-role">{userRole}</span>
                                </div>
                                {userData?.access?.education && userData?.access?.networking ? (
                                    <span
                                        className="settings-icon"
                                        title="Profile & Settings"
                                        onClick={(e) => { e.stopPropagation(); onTabChange('profile'); closeSidebarOnMobile(); }}
                                    >
                                        <Settings size={18} />
                                    </span>
                                ) : (
                                    <span
                                        className="upgrade-btn"
                                        title="Upgrade plan"
                                        onClick={(e) => { e.stopPropagation(); onTabChange('profile'); closeSidebarOnMobile(); setTimeout(() => window.dispatchEvent(new CustomEvent('open-plans')), 100); }}
                                    >
                                        <span>Upgrade</span>
                                    </span>
                                )}
                            </>
                        )}
                    </button>
                </div>
        </aside>

            {/* Tooltip rendered outside sidebar to avoid overflow clipping */}
            {tooltip && (
                <div
                    className="sidebar-icon-tooltip"
                    style={{ top: tooltip.top, left: tooltip.left }}
                >
                    {collapsed ? 'Open sidebar' : 'Close sidebar'}
                </div>
            )}

            {/* Edge hover zone */}
            <div
                className={`sidebar-edge-zone ${collapsed ? 'sidebar-edge-zone--collapsed' : ''}`}
                onClick={handleToggle}
            />

            {/* Context menu portal */}
            {contextMenu && createPortal(
                <div
                    className="context-menu"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {contextMenu.type === 'project' && (
                        <button
                            type="button"
                            className="context-menu-item"
                            onClick={() => { setEditingProjectId(contextMenu.id); setContextMenu(null); }}
                        >
                            <FileEdit size={14} />
                            Edit Project
                        </button>
                    )}
                    <button
                        type="button"
                        className="context-menu-item"
                        onClick={handleContextMenuRename}
                    >
                        <Pencil size={14} />
                        Rename
                    </button>
                    <button
                        type="button"
                        className="context-menu-item danger"
                        onClick={handleContextMenuDelete}
                    >
                        <Trash2 size={14} />
                        Delete
                    </button>
                </div>,
                document.body
            )}

            {/* Delete chat modal */}
            {confirmDeleteChatId && (
                <div className="rp-modal-overlay" onClick={() => setConfirmDeleteChatId(null)}>
                    <div className="rp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="rp-modal-title">Delete chat</div>
                        <div className="rp-modal-message">
                            Are you sure you want to delete "{directChats.find((c) => c.id === confirmDeleteChatId)?.name ?? 'this chat'}"? This action cannot be undone.
                        </div>
                        <div className="rp-modal-actions">
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setConfirmDeleteChatId(null)}
                                disabled={isDeletingChatId === confirmDeleteChatId}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="danger-btn"
                                onClick={() => executeDeleteDirectChat(confirmDeleteChatId)}
                                disabled={isDeletingChatId === confirmDeleteChatId}
                            >
                                {isDeletingChatId === confirmDeleteChatId ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Search modal — rendered via portal */}
            {searchOpen && createPortal(
                <div className="search-modal-backdrop" onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
                    <div className="search-modal" onClick={e => e.stopPropagation()}>
                        <div className="search-modal-header">
                            <Search size={18} className="search-modal-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="search-modal-input"
                                placeholder="Search chats and projects..."
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
                            />
                            <button type="button" className="search-modal-esc" onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
                                ESC
                            </button>
                        </div>
                        {sq ? (
                            <div className="search-results">
                                {isSearching && <div className="search-loading">Searching...</div>}

                                {(() => {
                                    // Use API results if available, otherwise client-side
                                    const apiItems = apiSearchResults?.results ?? [];
                                    const hasApi = apiItems.length > 0;

                                    // Client-side fallback: merge projects + chats with type discriminator
                                    const clientItems = hasApi ? [] : [
                                        ...filteredProjects.map(p => ({ type: 'project' as const, id: p.id, name: p.name })),
                                        ...allRecentChats.map(c => ({ type: 'chat' as const, id: c.id, name: c.name, project: c.projectId, project_name: c.projectName })),
                                    ];

                                    const items = hasApi ? apiItems : clientItems;

                                    // Group by type for display
                                    const projectItems = items.filter((r: any) => r.type === 'project');
                                    const chatItems = items.filter((r: any) => r.type === 'chat');
                                    const noResults = !isSearching && items.length === 0;

                                    return (
                                        <>
                                            {noResults && <div className="search-no-results">No results for "{searchQuery}"</div>}
                                            {projectItems.length > 0 && (
                                                <div className="search-result-section">
                                                    <span className="search-result-label">Projects</span>
                                                    {projectItems.map((r: any) => (
                                                        <button key={r.id} type="button" className="search-result-item" onClick={() => { onTabChange('assistant'); toggleProject(r.id); setSearchOpen(false); setSearchQuery(''); setApiSearchResults(null); }}>
                                                            <Folder size={16} />
                                                            <span>{r.name}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {chatItems.length > 0 && (
                                                <div className="search-result-section">
                                                    <span className="search-result-label">{hasApi ? 'Messages & Chats' : 'Chats'}</span>
                                                    {chatItems.map((r: any) => (
                                                        <button key={r.id} type="button" className="search-result-item" onClick={() => { onChatSelect?.(r.project || '', r.id); setSearchOpen(false); setSearchQuery(''); setApiSearchResults(null); closeSidebarOnMobile(); }}>
                                                            <MessageSquare size={16} />
                                                            <span>{r.name}</span>
                                                            {r.project_name && <span className="chat-project-tag">{r.project_name}</span>}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {apiSearchResults?.next && (
                                                <button type="button" className="search-load-more" onClick={async () => {
                                                    if (!apiSearchResults?.next || isSearching) return;
                                                    setIsSearching(true);
                                                    try {
                                                        const res = await apiClient.get(apiSearchResults.next);
                                                        setApiSearchResults(prev => prev ? {
                                                            results: [...prev.results, ...res.data.results],
                                                            next: res.data.next,
                                                        } : res.data);
                                                    } catch {}
                                                    setIsSearching(false);
                                                }}>
                                                    {isSearching ? 'Loading...' : 'Load more'}
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        ) : (
                            <div className="search-results">
                                <div className="search-result-section">
                                    <span className="search-result-label">Recent</span>
                                    {allRecentChats.slice(0, 10).map(c => (
                                        <button key={c.id} type="button" className="search-result-item" onClick={() => { onChatSelect?.(c.projectId, c.id); setSearchOpen(false); setSearchQuery(''); closeSidebarOnMobile(); }}>
                                            <MessageSquare size={16} />
                                            <span>{c.name}</span>
                                            {c.projectName && <span className="chat-project-tag">{c.projectName}</span>}
                                        </button>
                                    ))}
                                </div>
                                {projects.length > 0 && (
                                    <div className="search-result-section">
                                        <span className="search-result-label">Projects</span>
                                        {projects.slice(0, 5).map(p => (
                                            <button key={p.id} type="button" className="search-result-item" onClick={() => { onTabChange('assistant'); toggleProject(p.id); setSearchOpen(false); setSearchQuery(''); }}>
                                                <Folder size={16} />
                                                <span>{p.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {/* Delete project modal */}
            {confirmDeleteProjectId && (
                <div className="rp-modal-overlay" onClick={() => setConfirmDeleteProjectId(null)}>
                    <div className="rp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="rp-modal-title">Delete project</div>
                        <div className="rp-modal-message">
                            Are you sure you want to delete "{projects.find((p) => p.id === confirmDeleteProjectId)?.name ?? 'this project'}" and all its chats? This action cannot be undone.
                        </div>
                        <div className="rp-modal-actions">
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setConfirmDeleteProjectId(null)}
                                disabled={isDeletingProjectId === confirmDeleteProjectId}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="danger-btn"
                                onClick={() => executeDeleteProject(confirmDeleteProjectId)}
                                disabled={isDeletingProjectId === confirmDeleteProjectId}
                            >
                                {isDeletingProjectId === confirmDeleteProjectId ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete project chat modal */}
            {confirmDeleteProjectChatId && (
                <div className="rp-modal-overlay" onClick={() => setConfirmDeleteProjectChatId(null)}>
                    <div className="rp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="rp-modal-title">Delete chat</div>
                        <div className="rp-modal-message">
                            Are you sure you want to delete "{projects.find((p) => p.id === confirmDeleteProjectChatId.projectId)?.chats.find((c) => c.id === confirmDeleteProjectChatId.chatId)?.name ?? 'this chat'}"? This action cannot be undone.
                        </div>
                        <div className="rp-modal-actions">
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setConfirmDeleteProjectChatId(null)}
                                disabled={isDeletingProjectChatId === confirmDeleteProjectChatId.chatId}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="danger-btn"
                                onClick={() => executeDeleteProjectChat(confirmDeleteProjectChatId.projectId, confirmDeleteProjectChatId.chatId)}
                                disabled={isDeletingProjectChatId === confirmDeleteProjectChatId.chatId}
                            >
                                {isDeletingProjectChatId === confirmDeleteProjectChatId.chatId ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Create Project Modal */}
            {showCreateProjectModal && (
                <ProjectEditModal
                    mode="create"
                    onClose={() => setShowCreateProjectModal(false)}
                    onSaved={(created) => {
                        const projectWithChats: Project = {
                            id: created.id,
                            name: created.name,
                            description: created.description,
                            files: created.files,
                            chats: [],
                        };
                        setProjects(prev => [projectWithChats, ...prev]);
                        setExpandedProjects(prev => new Set([...prev, created.id]));
                    }}
                />
            )}

            {/* Project Edit Modal */}
            {editingProjectId && (() => {
                const proj = projects.find(p => p.id === editingProjectId);
                if (!proj) return null;
                return (
                    <ProjectEditModal
                        mode="edit"
                        projectId={proj.id}
                        name={proj.name}
                        description={proj.description || ''}
                        files={proj.files || []}
                        onClose={() => setEditingProjectId(null)}
                        onSaved={(updated) => {
                            setProjects(prev => prev.map(p =>
                                p.id === editingProjectId
                                    ? { ...p, name: updated.name, description: updated.description, files: updated.files }
                                    : p
                            ));
                        }}
                    />
                );
            })()}
        </>
    );
}

export default Sidebar;
